import { type AnnualCashFlow } from '@fruitful/financial-calculators';
import { useMemo } from 'react';

import { getYearIndex } from '../utils/retirementCalculatorUtils';

interface AnnualCashFlowTotals {
    totalEndingAssets: number;
    totalContributions: number;
    totalGrowth: number;
    totalDistributions: number;
    totalDistributionTaxes: number;
    totalSocialSecurityTaxes: number;
    totalTaxes: number; // totalDistributionTaxes + totalSocialSecurityTaxes
    totalSocialSecurity: number;
    totalRetirementExpenses: number;
    totalInflow: number;
}

export const useAnnualCashFlowTotals = (
    annualCashFlows: AnnualCashFlow[],
    selectedYear: number,
): AnnualCashFlowTotals | null => {
    return useMemo(() => {
        const selectedYearIndex = getYearIndex(selectedYear);
        const cashFlow = annualCashFlows[selectedYearIndex];

        if (annualCashFlows.length === 0 || cashFlow == null) {
            return null;
        }

        // Calculate totals for each category
        const totalEndingAssets = cashFlow.endingAssets.reduce(
            (sum, account) => sum + account.balance,
            0,
        );

        const totalContributions = cashFlow.contributions.reduce(
            (sum, contribution) => sum + contribution.totalContribution,
            0,
        );

        const totalGrowth = cashFlow.growth.reduce(
            (sum, g) => sum + (g.growth ?? 0),
            0,
        );

        const totalDistributions = cashFlow.distributions.reduce(
            (sum, distribution) => sum + distribution.grossDistributionAmount,
            0,
        );

        const totalDistributionTaxes = cashFlow.distributions.reduce(
            (sum, distribution) => sum + distribution.taxAmount,
            0,
        );

        const totalSocialSecurityTaxes = cashFlow.totalSocialSecurityTax;

        const totalTaxes = totalDistributionTaxes + totalSocialSecurityTaxes;

        const totalSocialSecurity =
            (cashFlow.primaryMemberSocialSecurityBenefit ?? 0) +
            (cashFlow.partnerMemberSocialSecurityBenefit ?? 0);

        const totalRetirementExpenses = cashFlow.totalRetirementExpenses;

        // Total inflows (contributions + growth + net social security + net distributions after taxes)
        const netDistributions = totalDistributions - totalDistributionTaxes;
        const netSocialSecurity =
            totalSocialSecurity - totalSocialSecurityTaxes;
        const totalInflow =
            totalContributions +
            totalGrowth +
            netSocialSecurity +
            netDistributions;

        return {
            totalEndingAssets,
            totalContributions,
            totalGrowth,
            totalDistributions,
            totalDistributionTaxes,
            totalSocialSecurityTaxes,
            totalTaxes,
            totalSocialSecurity,
            totalRetirementExpenses,
            totalInflow,
        };
    }, [annualCashFlows, selectedYear]);
};
